import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Users, IndianRupee, TrendingUp, Shield, Loader2, Search,
  BarChart3, Clock, FileText, AlertTriangle, Settings, Eye,
  ChevronRight, ArrowUpRight, ArrowDownRight, CheckCircle2,
  Calculator, Briefcase, Activity, Zap, Lock, Sparkles, Save, CreditCard, Tag, RefreshCw, XCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (a) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(a || 0);

const SECTIONS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "users", label: "Users", icon: Users },
  { id: "professionals", label: "Professionals", icon: Calculator },
  { id: "trials", label: "Trials & Conversion", icon: Zap },
  { id: "usage", label: "Product Usage", icon: Activity },
  { id: "invoices", label: "Invoices", icon: FileText },
  { id: "subscriptions", label: "Subscriptions", icon: CreditCard },
  { id: "pricing", label: "Pricing", icon: Tag },
  { id: "seo", label: "SEO & Blog", icon: Sparkles, href: "/admin/seo" },
  { id: "settings", label: "Settings", icon: Settings },
];

function KPICard({ label, value, sub, icon: Icon, color = "blue" }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
      <div className="flex items-center gap-2.5 mb-2">
        <div className={`w-8 h-8 rounded-lg bg-${color}-50 flex items-center justify-center`}>
          <Icon className={`w-4 h-4 text-${color}-600`} />
        </div>
        <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-bold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function FunnelBar({ label, count, maxCount, color }) {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-600 w-36 text-right">{label}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-7 relative overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${Math.max(pct, 2)}%` }} />
        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-slate-700">{count}</span>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [chartData, setChartData] = useState({});
  const [usersData, setUsersData] = useState({ users: [], total: 0, page: 1, pages: 0 });
  const [proData, setProData] = useState(null);
  const [trialsData, setTrialsData] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [settingsData, setSettingsData] = useState(null);
  const [subInsights, setSubInsights] = useState(null);
  const [pricingCfg, setPricingCfg] = useState(null);
  const [rzpConfig, setRzpConfig] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || user?.role !== "admin")) {
      navigate("/dashboard");
      return;
    }
    if (isAuthenticated && user?.role === "admin") fetchOverview();
  }, [isAuthenticated, authLoading]);

  const fetchOverview = async () => {
    try {
      const [ovRes, planRes, funnelRes] = await Promise.all([
        axios.get(`${API}/admin/overview`, { withCredentials: true }),
        axios.get(`${API}/admin/charts/plan-distribution`, { withCredentials: true }),
        axios.get(`${API}/admin/charts/conversion-funnel`, { withCredentials: true }),
      ]);
      setOverview(ovRes.data);
      setChartData(prev => ({ ...prev, planDist: planRes.data.data, funnel: funnelRes.data.data }));
    } catch (err) {
      if (err.response?.status === 403) navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (userSearch) params.append("search", userSearch);
      if (userFilter) params.append(userFilter === "ca" ? "role" : "plan", userFilter === "ca" ? "ca" : userFilter);
      const res = await axios.get(`${API}/admin/users?${params}`, { withCredentials: true });
      setUsersData(res.data);
    } catch { /* */ }
  }, [userSearch, userFilter]);

  useEffect(() => { if (section === "users") fetchUsers(); }, [section, fetchUsers]);
  useEffect(() => {
    if (section === "professionals" && !proData) axios.get(`${API}/admin/professionals`, { withCredentials: true }).then(r => setProData(r.data)).catch(() => {});
    if (section === "trials" && !trialsData) axios.get(`${API}/admin/trials`, { withCredentials: true }).then(r => setTrialsData(r.data)).catch(() => {});
    if (section === "usage" && !usageData) axios.get(`${API}/admin/product-usage`, { withCredentials: true }).then(r => setUsageData(r.data)).catch(() => {});
    if (section === "invoices" && !invoiceData) axios.get(`${API}/admin/invoice-insights`, { withCredentials: true }).then(r => setInvoiceData(r.data)).catch(() => {});
    if (section === "settings" && !settingsData) axios.get(`${API}/admin/settings`, { withCredentials: true }).then(r => setSettingsData(r.data)).catch(() => {});
    if (section === "subscriptions" && !subInsights) axios.get(`${API}/admin/subscription-insights`, { withCredentials: true }).then(r => setSubInsights(r.data)).catch(() => {});
    if (section === "pricing" && !pricingCfg) axios.get(`${API}/admin/pricing`, { withCredentials: true }).then(r => setPricingCfg(r.data)).catch(() => {});
    if (section === "pricing" && !rzpConfig) axios.get(`${API}/admin/razorpay-config`, { withCredentials: true }).then(r => setRzpConfig(r.data)).catch(() => {});
  }, [section]);

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      const res = await axios.put(`${API}/admin/pricing`, pricingCfg, { withCredentials: true });
      setPricingCfg(res.data);
      toast.success("Pricing updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingPricing(false);
    }
  };

  const handleUserAction = async (userId, action, extra = {}) => {
    try {
      if (action === "extend-trial") {
        await axios.post(`${API}/admin/users/${userId}/extend-trial`, { days: 7 }, { withCredentials: true });
        toast.success("Trial extended by 7 days");
      } else if (action === "disable") {
        if (!window.confirm("Disable this user?")) return;
        await axios.post(`${API}/admin/users/${userId}/disable`, {}, { withCredentials: true });
        toast.success("User disabled");
      } else if (action === "enable") {
        await axios.post(`${API}/admin/users/${userId}/enable`, {}, { withCredentials: true });
        toast.success("User enabled");
      } else if (action === "change-plan") {
        await axios.post(`${API}/admin/users/${userId}/change-plan`, { plan: extra.plan }, { withCredentials: true });
        toast.success(`Plan changed to ${extra.plan}`);
      }
      fetchUsers(usersData.page);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/settings`, settingsData, { withCredentials: true });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading || authLoading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  const ov = overview || {};
  const u = ov.users || {};
  const r = ov.revenue || {};
  const s = ov.subscriptions || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-56 bg-slate-900 min-h-screen p-4 flex-shrink-0 hidden lg:block" data-testid="admin-sidebar">
          <div className="flex items-center gap-2 mb-8 px-2">
            <Shield className="w-5 h-5 text-violet-400" />
            <span className="text-sm font-bold text-white">Admin Panel</span>
          </div>
          <nav className="space-y-1">
            {SECTIONS.map(sec => (
              <button key={sec.id} onClick={() => sec.href ? navigate(sec.href) : setSection(sec.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  section === sec.id ? "bg-white/10 text-white font-medium" : "text-slate-400 hover:text-white hover:bg-white/5"
                }`} data-testid={`admin-tab-${sec.id}`}>
                <sec.icon className="w-4 h-4" /> {sec.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Mobile tabs */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 flex overflow-x-auto px-2 py-1.5">
          {SECTIONS.map(sec => (
            <button key={sec.id} onClick={() => sec.href ? navigate(sec.href) : setSection(sec.id)}
              className={`flex flex-col items-center gap-0.5 px-3 py-1 min-w-[60px] text-[10px] ${
                section === sec.id ? "text-violet-600 font-bold" : "text-slate-400"
              }`} data-testid={`admin-tab-mobile-${sec.id}`}>
              <sec.icon className="w-4 h-4" /> {sec.label}
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 pb-20 lg:pb-6 overflow-auto">

          {/* ==================== OVERVIEW ==================== */}
          {section === "overview" && (
            <div data-testid="admin-overview">
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Dashboard Overview</h1>
              <p className="text-sm text-slate-500 mb-6">Platform health at a glance</p>

              {/* User KPIs */}
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Users</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <KPICard label="Total Users" value={u.total} icon={Users} color="blue" />
                <KPICard label="Business Owners" value={u.business_owners} icon={Briefcase} color="emerald" />
                <KPICard label="CA / Professionals" value={u.ca_professionals} icon={Calculator} color="violet" />
                <KPICard label="New This Month" value={u.new_month} sub={`${u.new_today} today`} icon={TrendingUp} color="amber" />
              </div>

              {/* Revenue KPIs */}
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Revenue</h3>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
                <KPICard label="MRR" value={fmt(r.mrr)} icon={IndianRupee} color="emerald" />
                <KPICard label="Lifetime Revenue" value={fmt(r.total_lifetime)} icon={IndianRupee} color="blue" />
                <KPICard label="This Month" value={fmt(r.this_month)} icon={TrendingUp} color="violet" />
                <KPICard label="Today" value={fmt(r.today)} icon={IndianRupee} color="amber" />
                <KPICard label="ARPU" value={fmt(r.arpu)} icon={Users} color="slate" />
              </div>

              {/* Subscription KPIs */}
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Subscriptions</h3>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
                <KPICard label="Free Users" value={s.free} icon={Users} color="slate" />
                <KPICard label="Trial Active" value={s.trial_active} icon={Sparkles} color="violet" />
                <KPICard label="Growth Monthly" value={s.growth_monthly} icon={Zap} color="blue" />
                <KPICard label="Yearly" value={s.yearly} icon={CheckCircle2} color="emerald" />
                <KPICard label="Trial→Paid %" value={`${s.trial_to_paid_pct}%`} icon={TrendingUp} color="amber" />
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Plan Distribution */}
                {chartData.planDist && (
                  <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm" data-testid="plan-dist-chart">
                    <h3 className="text-sm font-bold text-slate-900 mb-4">Plan Distribution</h3>
                    <div className="space-y-2.5">
                      {chartData.planDist.map(p => {
                        const total = chartData.planDist.reduce((a, b) => a + b.value, 0);
                        const pct = total > 0 ? Math.round(p.value / total * 100) : 0;
                        return (
                          <div key={p.name} className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                            <span className="text-sm text-slate-700 w-16">{p.name}</span>
                            <div className="flex-1 bg-slate-100 rounded-full h-5 relative overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: p.color }} />
                            </div>
                            <span className="text-xs font-bold text-slate-600 w-12 text-right">{p.value} ({pct}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Conversion Funnel */}
                {chartData.funnel && (
                  <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm" data-testid="conversion-funnel">
                    <h3 className="text-sm font-bold text-slate-900 mb-4">Conversion Funnel</h3>
                    <div className="space-y-3">
                      {chartData.funnel.map((f, i) => (
                        <FunnelBar key={f.stage} label={f.stage} count={f.count} maxCount={chartData.funnel[0]?.count || 1}
                          color={["bg-blue-400", "bg-violet-400", "bg-amber-400", "bg-emerald-500"][i] || "bg-slate-400"} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ==================== USERS ==================== */}
          {section === "users" && (
            <div data-testid="admin-users">
              <h1 className="text-2xl font-bold text-slate-900 mb-1">User Management</h1>
              <p className="text-sm text-slate-500 mb-4">{usersData.total} total users</p>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={userSearch} onChange={e => setUserSearch(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && fetchUsers(1)}
                    placeholder="Search name or email..." className="pl-9 text-sm" data-testid="admin-user-search" />
                </div>
                {["", "free", "trial", "growth", "yearly", "ca"].map(f => (
                  <button key={f} onClick={() => { setUserFilter(f); setTimeout(() => fetchUsers(1), 100); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      userFilter === f ? "bg-violet-100 text-violet-700 border-violet-200" : "bg-white text-slate-500 border-slate-200"
                    }`} data-testid={`filter-${f || "all"}`}>
                    {f === "" ? "All" : f === "ca" ? "CA/Pro" : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>

              <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500">Name</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500">Email</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500">Role</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500">Plan</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500">Trial</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">Invoices</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">Revenue</th>
                      <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {usersData.users.map(u => (
                      <tr key={u.user_id} className="hover:bg-slate-50" data-testid={`user-row-${u.user_id}`}>
                        <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{u.email}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            u.role === "ca" ? "bg-violet-50 text-violet-700" : u.role === "admin" ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700"
                          }`}>{u.role}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            u.plan === "growth" ? "bg-blue-50 text-blue-700" : u.plan === "yearly" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"
                          }`}>{u.plan || "free"}</span>
                        </td>
                        <td className="px-4 py-3 text-center text-xs">{u.trial_active ? "Active" : "—"}</td>
                        <td className="px-4 py-3 text-right text-xs font-medium">{u.invoice_count}</td>
                        <td className="px-4 py-3 text-right text-xs font-medium">{u.revenue_generated > 0 ? fmt(u.revenue_generated) : "—"}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => handleUserAction(u.user_id, "extend-trial")} className="text-[10px] text-violet-600 hover:underline px-1" data-testid={`extend-${u.user_id}`}>+Trial</button>
                            <select onChange={e => { if (e.target.value) handleUserAction(u.user_id, "change-plan", { plan: e.target.value }); e.target.value = ""; }}
                              className="text-[10px] border rounded px-1 py-0.5 text-slate-600" data-testid={`plan-select-${u.user_id}`}>
                              <option value="">Plan</option>
                              <option value="free">Free</option>
                              <option value="growth">Growth</option>
                              <option value="yearly">Yearly</option>
                            </select>
                            {u.disabled ? (
                              <button onClick={() => handleUserAction(u.user_id, "enable")} className="text-[10px] text-emerald-600 hover:underline px-1">Enable</button>
                            ) : (
                              <button onClick={() => handleUserAction(u.user_id, "disable")} className="text-[10px] text-red-500 hover:underline px-1">Disable</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {usersData.pages > 1 && (
                  <div className="flex items-center justify-between p-3 border-t border-slate-100">
                    <span className="text-xs text-slate-500">Page {usersData.page} of {usersData.pages}</span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" disabled={usersData.page <= 1} onClick={() => fetchUsers(usersData.page - 1)} className="text-xs">Prev</Button>
                      <Button size="sm" variant="outline" disabled={usersData.page >= usersData.pages} onClick={() => fetchUsers(usersData.page + 1)} className="text-xs">Next</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ==================== PROFESSIONALS ==================== */}
          {section === "professionals" && proData && (
            <div data-testid="admin-professionals">
              <h1 className="text-2xl font-bold text-slate-900 mb-1">CA / Professional Network</h1>
              <p className="text-sm text-slate-500 mb-4">{proData.total} total professionals</p>

              <div className="grid grid-cols-3 gap-3 mb-6">
                <KPICard label="Total Professionals" value={proData.total} icon={Calculator} color="violet" />
                <KPICard label="Active (1+ Clients)" value={proData.active} icon={CheckCircle2} color="emerald" />
                <KPICard label="With Linked Clients" value={proData.with_clients} icon={Users} color="blue" />
              </div>

              <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-x-auto">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="text-sm font-semibold text-slate-900">Professionals by Client Count</h3>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500">Name</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500">Email</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500">Pro ID</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">Active Clients</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">Total Clients</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {proData.professionals.map(p => (
                      <tr key={p.user_id} className="hover:bg-slate-50" data-testid={`pro-row-${p.user_id}`}>
                        <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{p.email}</td>
                        <td className="px-4 py-3 text-xs font-mono text-violet-600">{p.professional_id}</td>
                        <td className="px-4 py-3 text-right font-bold text-emerald-700">{p.active_clients}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{p.total_clients}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ==================== TRIALS & CONVERSION ==================== */}
          {section === "trials" && trialsData && (
            <div data-testid="admin-trials">
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Trials & Conversion</h1>
              <p className="text-sm text-slate-500 mb-4">Track trial performance and conversions</p>

              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
                <KPICard label="Active Trials" value={trialsData.trial_active} icon={Sparkles} color="violet" />
                <KPICard label="Expiring 7 Days" value={trialsData.expiring_7_days} icon={Clock} color="amber" />
                <KPICard label="Expired (Not Converted)" value={trialsData.expired_not_converted} icon={AlertTriangle} color="red" />
                <KPICard label="Total Paid" value={trialsData.paid_total} icon={CheckCircle2} color="emerald" />
                <KPICard label="Conversion %" value={`${trialsData.conversion_pct}%`} icon={TrendingUp} color="blue" />
              </div>

              {trialsData.top_features_before_upgrade?.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm" data-testid="top-features-upgrade">
                  <h3 className="text-sm font-bold text-slate-900 mb-4">Most Accessed Features (before upgrade)</h3>
                  <div className="space-y-2">
                    {trialsData.top_features_before_upgrade.map((f, i) => (
                      <div key={f.feature} className="flex items-center gap-3">
                        <span className="text-xs font-bold text-slate-400 w-5">{i + 1}</span>
                        <span className="text-sm text-slate-700 flex-1">{f.feature.replace(/_/g, " ")}</span>
                        <span className="text-xs font-bold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">{f.count} attempts</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ==================== PRODUCT USAGE ==================== */}
          {section === "usage" && usageData && (
            <div data-testid="admin-usage">
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Product Usage</h1>
              <p className="text-sm text-slate-500 mb-4">What users want most</p>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
                  <h3 className="text-sm font-bold text-slate-900 mb-3">Most Accessed Features</h3>
                  {usageData.feature_attempts?.map((f, i) => (
                    <div key={f.feature} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                      <span className="text-xs text-slate-700">{f.feature.replace(/_/g, " ")}</span>
                      <span className="text-xs font-bold text-slate-600">{f.count}</span>
                    </div>
                  ))}
                  {(!usageData.feature_attempts || usageData.feature_attempts.length === 0) && <p className="text-xs text-slate-400">No data yet</p>}
                </div>

                <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
                  <h3 className="text-sm font-bold text-slate-900 mb-3">Locked Feature Attempts</h3>
                  {usageData.locked_attempts?.map((f, i) => (
                    <div key={f.feature} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                      <span className="text-xs text-slate-700">{f.feature.replace(/_/g, " ")}</span>
                      <span className="text-xs font-bold text-red-600">{f.count} blocked</span>
                    </div>
                  ))}
                  {(!usageData.locked_attempts || usageData.locked_attempts.length === 0) && <p className="text-xs text-slate-400">No blocked attempts yet</p>}
                </div>

                <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
                  <h3 className="text-sm font-bold text-slate-900 mb-3">Temporary Unlocks</h3>
                  {usageData.unlock_attempts?.map((f, i) => (
                    <div key={f.feature} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                      <span className="text-xs text-slate-700">{f.feature.replace(/_/g, " ")}</span>
                      <span className="text-xs font-bold text-violet-600">{f.count} unlocks</span>
                    </div>
                  ))}
                  {(!usageData.unlock_attempts || usageData.unlock_attempts.length === 0) && <p className="text-xs text-slate-400">No unlocks yet</p>}
                </div>
              </div>
            </div>
          )}

          {/* ==================== INVOICES ==================== */}
          {section === "invoices" && invoiceData && (
            <div data-testid="admin-invoices">
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Invoice Insights</h1>
              <p className="text-sm text-slate-500 mb-4">Platform usage and value</p>

              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
                <KPICard label="Total Invoices" value={invoiceData.total_invoices} icon={FileText} color="blue" />
                <KPICard label="Today" value={invoiceData.today} icon={Clock} color="amber" />
                <KPICard label="Avg / User" value={invoiceData.avg_per_user} icon={Users} color="violet" />
                <KPICard label="Users with Invoices" value={invoiceData.total_users_with_invoices} icon={Briefcase} color="emerald" />
                <KPICard label="Total Value" value={fmt(invoiceData.total_value_processed)} icon={IndianRupee} color="blue" />
              </div>

              {invoiceData.top_businesses?.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-x-auto">
                  <div className="p-4 border-b border-slate-100">
                    <h3 className="text-sm font-semibold text-slate-900">Top Active Businesses</h3>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50">
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500">Business</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">Invoices</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500">Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {invoiceData.top_businesses.map((b, i) => (
                        <tr key={b._id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900">{b.name || "Unknown"}</p>
                            <p className="text-xs text-slate-500">{b.email}</p>
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-slate-700">{b.count}</td>
                          <td className="px-4 py-3 text-right font-bold text-emerald-700">{fmt(b.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ==================== SUBSCRIPTIONS ==================== */}
          {section === "subscriptions" && (
            <div data-testid="admin-subscriptions">
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Subscription Insights</h1>
              <p className="text-sm text-slate-500 mb-4">Trial pipeline, paid users, revenue rollup, recent payments and renewals</p>
              {!subInsights ? (
                <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" /></div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                    <KPICard label="Total Users" value={subInsights.counts.total_users} icon={Users} color="blue" />
                    <KPICard label="Trial Users" value={subInsights.counts.trial_users} icon={Zap} color="violet" />
                    <KPICard label="Paid Growth" value={subInsights.counts.paid_growth_users} icon={TrendingUp} color="emerald" />
                    <KPICard label="Paid Yearly" value={subInsights.counts.paid_yearly_users} icon={Sparkles} color="blue" />
                    <KPICard label="Free Users" value={subInsights.counts.free_users} icon={Users} color="blue" />
                    <KPICard label="Expired Trials" value={subInsights.counts.expired_trials} icon={Clock} color="blue" />
                    <KPICard label="Promo Subs" value={subInsights.counts.promotional_subscriptions} icon={Tag} color="violet" />
                    <KPICard label="Failed Payments" value={subInsights.counts.failed_payments} icon={XCircle} color="blue" />
                  </div>
                  <div className="grid md:grid-cols-3 gap-3 mb-5">
                    <KPICard label="Monthly Revenue (Growth)" value={fmt(subInsights.revenue.monthly_growth)} icon={IndianRupee} color="emerald" />
                    <KPICard label="Yearly Revenue" value={fmt(subInsights.revenue.yearly)} icon={IndianRupee} color="emerald" />
                    <KPICard label="Total Revenue" value={fmt(subInsights.revenue.total)} icon={TrendingUp} color="violet" />
                  </div>

                  <div className="bg-white rounded-xl border border-slate-100 p-5 mb-5" data-testid="recent-payments-table">
                    <h2 className="font-bold text-slate-900 mb-3">Recent Payments</h2>
                    {subInsights.recent_payments.length === 0 ? (
                      <p className="text-xs text-slate-400 py-4">No payments yet.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead><tr className="text-[11px] uppercase text-slate-500 border-b border-slate-100"><th className="py-2 text-left">Order</th><th className="py-2 text-left">User</th><th className="py-2 text-left">Plan</th><th className="py-2 text-right">Amount</th><th className="py-2 text-left">Verified</th></tr></thead>
                          <tbody>
                          {subInsights.recent_payments.map((p) => (
                            <tr key={p.order_id} className="border-b border-slate-50">
                              <td className="py-2 font-mono text-[11px]">{p.order_id}</td>
                              <td className="py-2 text-slate-700">{p.user_id}</td>
                              <td className="py-2"><span className="text-[10px] font-semibold bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded">{p.plan}</span></td>
                              <td className="py-2 text-right font-medium">{fmt((p.amount || 0) / 100)}</td>
                              <td className="py-2 text-xs text-slate-500">{(p.verified_at || "").slice(0, 10)}</td>
                            </tr>
                          ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="bg-white rounded-xl border border-slate-100 p-5 mb-5" data-testid="upcoming-renewals-table">
                    <h2 className="font-bold text-slate-900 mb-3">Upcoming Renewals</h2>
                    {subInsights.upcoming_renewals.length === 0 ? (
                      <p className="text-xs text-slate-400 py-4">No upcoming renewals.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead><tr className="text-[11px] uppercase text-slate-500 border-b border-slate-100"><th className="py-2 text-left">User</th><th className="py-2 text-left">Plan</th><th className="py-2 text-left">Auto-renew</th><th className="py-2 text-left">Expiry</th></tr></thead>
                          <tbody>
                          {subInsights.upcoming_renewals.map((u) => (
                            <tr key={u.user_id} className="border-b border-slate-50">
                              <td className="py-2 text-slate-700 text-xs">{u.email}</td>
                              <td className="py-2"><span className="text-[10px] font-semibold bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{u.plan}</span></td>
                              <td className="py-2">{u.auto_renew ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-slate-300" />}</td>
                              <td className="py-2 text-xs text-slate-500">{(u.plan_expiry || "").slice(0, 10)}</td>
                            </tr>
                          ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {subInsights.recent_failed.length > 0 && (
                    <div className="bg-white rounded-xl border border-red-100 p-5" data-testid="failed-payments-table">
                      <h2 className="font-bold text-slate-900 mb-3 inline-flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /> Failed Payments</h2>
                      <table className="w-full text-sm">
                        <tbody>
                        {subInsights.recent_failed.map((p) => (
                          <tr key={p.order_id} className="border-b border-slate-50">
                            <td className="py-2 font-mono text-[11px]">{p.order_id}</td>
                            <td className="py-2 text-xs text-red-600">{p.failure_reason || "Unknown"}</td>
                            <td className="py-2 text-right">{fmt((p.amount || 0) / 100)}</td>
                          </tr>
                        ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ==================== PRICING ==================== */}
          {section === "pricing" && (
            <div data-testid="admin-pricing">
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Pricing Configuration</h1>
              <p className="text-sm text-slate-500 mb-4">Edit values shown on the public Pricing page and used at checkout.</p>

              {/* Razorpay AutoPay readiness diagnostic */}
              {rzpConfig && (
                <div
                  className={`rounded-xl border p-4 mb-5 ${rzpConfig.autopay_ready ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}
                  data-testid="rzp-autopay-status"
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 ${rzpConfig.autopay_ready ? "text-emerald-600" : "text-amber-600"}`}>
                      {rzpConfig.autopay_ready ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-900">
                        UPI AutoPay {rzpConfig.autopay_ready ? "READY" : "NOT CONFIGURED"}
                      </p>
                      <p className="text-xs text-slate-600 mt-0.5">
                        {rzpConfig.autopay_ready
                          ? `Razorpay ${rzpConfig.test_mode ? "TEST" : "LIVE"} mode · GPay / bank auto-debit mandate will be requested at checkout.`
                          : "Auto-debit mandate will NOT be requested. Add the env vars below to enable."}
                      </p>
                      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-slate-700">
                        <span>Key ID: {rzpConfig.key_id_set ? <span className="text-emerald-700">{rzpConfig.key_id_prefix}</span> : <span className="text-red-600">MISSING</span>}</span>
                        <span>Key Secret: {rzpConfig.key_secret_set ? <span className="text-emerald-700">set</span> : <span className="text-red-600">MISSING</span>}</span>
                        <span>Plan Growth: {rzpConfig.plan_growth_id_set ? <span className="text-emerald-700">{rzpConfig.plan_growth_id}</span> : <span className="text-red-600">MISSING</span>}</span>
                        <span>Plan Yearly: {rzpConfig.plan_yearly_id_set ? <span className="text-emerald-700">{rzpConfig.plan_yearly_id}</span> : <span className="text-red-600">MISSING</span>}</span>
                        <span>Webhook secret: {rzpConfig.webhook_secret_set ? <span className="text-emerald-700">set</span> : <span className="text-amber-700">missing</span>}</span>
                        <span>Mode: {rzpConfig.live_mode ? <span className="text-emerald-700">LIVE</span> : <span className="text-amber-700">TEST</span>}</span>
                      </div>
                      {(rzpConfig.warnings || []).length > 0 && (
                        <ul className="mt-3 list-disc pl-4 text-[11px] text-amber-800 space-y-0.5">
                          {rzpConfig.warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      )}
                      {!rzpConfig.autopay_ready && (
                        <details className="mt-3 text-xs text-slate-700">
                          <summary className="cursor-pointer font-semibold text-slate-900">How to enable UPI AutoPay (1 minute)</summary>
                          <ol className="list-decimal pl-5 mt-2 space-y-1">
                            <li>Open Razorpay Dashboard → <code className="bg-white px-1 rounded">Subscriptions → Plans</code></li>
                            <li>Create a plan named "InvoiceSetu Growth" with amount <strong>₹{pricingCfg?.monthly_original || 499}</strong>, interval = Monthly. Copy its plan ID (starts with <code>plan_</code>).</li>
                            <li>Create another plan "InvoiceSetu Yearly" — amount <strong>₹{pricingCfg?.yearly_original || 4999}</strong>, interval = Yearly. Copy its plan ID.</li>
                            <li>Add to <code className="bg-white px-1 rounded">/app/backend/.env</code>:<br/>
                              <code className="block bg-white p-2 mt-1 rounded text-[11px]">
                                RAZORPAY_PLAN_GROWTH=plan_xxxxxxxxxxxx<br/>
                                RAZORPAY_PLAN_YEARLY=plan_yyyyyyyyyyyy<br/>
                                RAZORPAY_WEBHOOK_SECRET=your_webhook_secret
                              </code>
                            </li>
                            <li>Restart backend. Refresh this page — status should turn green.</li>
                          </ol>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!pricingCfg ? (
                <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" /></div>
              ) : (
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 max-w-2xl">
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      { key: "monthly_original", label: "Monthly Original Price (₹)", type: "number" },
                      { key: "monthly_discounted", label: "Monthly Discounted Price (₹)", type: "number" },
                      { key: "discount_months", label: "Discount Duration (months)", type: "number" },
                      { key: "first_charge_amount", label: "First Charge Amount (₹) — total upfront", type: "number" },
                      { key: "yearly_original", label: "Yearly Original Price (₹)", type: "number" },
                      { key: "yearly_discounted", label: "Yearly Discounted Price (₹)", type: "number" },
                      { key: "trial_days", label: "Trial Duration (days)", type: "number" },
                    ].map(field => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">{field.label}</label>
                        <Input
                          type={field.type}
                          value={pricingCfg[field.key] ?? ""}
                          onChange={e => setPricingCfg(prev => ({ ...prev, [field.key]: Number(e.target.value) }))}
                          data-testid={`pricing-${field.key}`}
                        />
                      </div>
                    ))}
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Pricing Banner Text</label>
                      <Input
                        value={pricingCfg.banner_text ?? ""}
                        onChange={e => setPricingCfg(prev => ({ ...prev, banner_text: e.target.value }))}
                        data-testid="pricing-banner_text"
                      />
                    </div>
                    <div className="sm:col-span-2 flex items-center justify-between bg-slate-50 rounded-lg p-3 border border-slate-100">
                      <span className="text-sm font-medium text-slate-700">Offer Active</span>
                      <button
                        onClick={() => setPricingCfg(prev => ({ ...prev, offer_active: !prev.offer_active }))}
                        className={`relative w-11 h-6 rounded-full transition-colors ${pricingCfg.offer_active ? "bg-violet-600" : "bg-slate-300"}`}
                        data-testid="pricing-offer_active"
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${pricingCfg.offer_active ? "translate-x-5" : ""}`} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-6 pt-5 border-t border-slate-100">
                    <Button onClick={savePricing} disabled={savingPricing} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-pricing-btn">
                      {savingPricing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Pricing
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ==================== SETTINGS ==================== */}
          {section === "settings" && settingsData && (
            <div data-testid="admin-settings">
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Platform Settings</h1>
              <p className="text-sm text-slate-500 mb-4">Configure business controls</p>

              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 max-w-2xl">
                <div className="space-y-5">
                  {[
                    { key: "trial_duration_days", label: "Trial Duration (days)", type: "number" },
                    { key: "invoice_history_free_limit", label: "Invoice History Free Limit", type: "number" },
                    { key: "growth_monthly_price", label: "Growth Monthly Price (INR)", type: "number" },
                    { key: "yearly_price", label: "Yearly Price (INR)", type: "number" },
                    { key: "max_unlocks_per_feature", label: "Max Unlocks Per Feature", type: "number" },
                    { key: "unlock_duration_days", label: "Unlock Duration (days)", type: "number" },
                  ].map(field => (
                    <div key={field.key}>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">{field.label}</label>
                      <Input type={field.type} value={settingsData[field.key] ?? ""}
                        onChange={e => setSettingsData(prev => ({ ...prev, [field.key]: field.type === "number" ? Number(e.target.value) : e.target.value }))}
                        className="max-w-xs" data-testid={`setting-${field.key}`} />
                    </div>
                  ))}

                  {[
                    { key: "smart_unlocks_enabled", label: "Smart Unlocks Enabled" },
                    { key: "branding_on_free", label: "Branding on Free Plan" },
                  ].map(toggle => (
                    <div key={toggle.key} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">{toggle.label}</span>
                      <button onClick={() => setSettingsData(prev => ({ ...prev, [toggle.key]: !prev[toggle.key] }))}
                        className={`relative w-11 h-6 rounded-full transition-colors ${settingsData[toggle.key] ? "bg-violet-600" : "bg-slate-300"}`}
                        data-testid={`toggle-${toggle.key}`}>
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${settingsData[toggle.key] ? "translate-x-5" : ""}`} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-5 border-t border-slate-100">
                  <Button onClick={saveSettings} disabled={savingSettings} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-settings-btn">
                    {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Settings
                  </Button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
